import React from "react";
import {
    Box, Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, Autocomplete, CircularProgress, Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useVendorLeadDetailContext } from "contexts/VendorLeadContext";
import {
    useAddVendorQuotationForPdfMailMutation,
    useLazyGetVendorQuotationsByLeadQuery,
} from "store/services/vendorQuotationApi";
import {
    useAddVendorQuotationExcelSendExcelMailMutation,
    useLazyGetVendorQuotationExcelsByLeadQuery,
} from "store/services/vendorQuotationExcelApi";

const isNoteValid = (text) => text.trim().length >= 3;

const RequoteModal = ({
    open,
    onClose,
    title,
    options,
    isFetching,
    selectedValue,
    onSelectionChange,
    attachLabel,
    noOptionsText,
    noteContent,
    setNoteContent,
    noteTouched,
    setNoteTouched,
    onSubmit,
    isLoading,
}) => {
    const isDisabled = isLoading || !selectedValue || !isNoteValid(noteContent);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Select a quotation assigned to this lead to send via email:
                    </Typography>

                    <Autocomplete
                        options={options}
                        getOptionLabel={(o) =>
                            `${o.title ?? ""} - ${o.vendorQuotationId ?? o.VendorQuotationExcelId ?? ""}`
                        }
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        loading={isFetching}
                        value={selectedValue}
                        onChange={(_, val) => onSelectionChange(val)}
                        noOptionsText={noOptionsText}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={attachLabel}
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isFetching && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    <TextField
                        label="Note"
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        onBlur={() => setNoteTouched(true)}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        placeholder="Add a note… (min. 3 characters)"
                        error={noteTouched && !isNoteValid(noteContent)}
                        helperText={
                            noteTouched && !isNoteValid(noteContent)
                                ? "Note must contain at least 3 characters"
                                : " "
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
                <Button variant="contained" onClick={onSubmit} disabled={isDisabled}>
                    {isLoading ? "Sending…" : "Send"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const RequoteButtons = () => {
    const { id } = useParams();
    const { vendorLeadDetail } = useVendorLeadDetailContext();

    // ── PDF modal state ──
    const [pdfOpen, setPdfOpen] = React.useState(false);
    const [selectedPdf, setSelectedPdf] = React.useState(null);
    const [pdfNote, setPdfNote] = React.useState("");
    const [pdfNoteTouched, setPdfNoteTouched] = React.useState(false);

    // ── Excel modal state ──
    const [excelOpen, setExcelOpen] = React.useState(false);
    const [selectedExcel, setSelectedExcel] = React.useState(null);
    const [excelNote, setExcelNote] = React.useState("");
    const [excelNoteTouched, setExcelNoteTouched] = React.useState(false);

    // ── API hooks ──
    const [sendPdfMail, { isLoading: sendingPdf }] = useAddVendorQuotationForPdfMailMutation();
    const [sendExcelMail, { isLoading: sendingExcel }] = useAddVendorQuotationExcelSendExcelMailMutation();

    const [fetchQuotationsByLead, { data: quotationOptions = [], isFetching: fetchingQuotations }] =
        useLazyGetVendorQuotationsByLeadQuery();

    const [fetchExcelsByLead, { data: excelOptions = [], isFetching: fetchingExcels }] =
        useLazyGetVendorQuotationExcelsByLeadQuery();

    // ── fetch on modal open ──
    const handleOpenPdf = () => {
        setPdfOpen(true);
        fetchQuotationsByLead(id);
    };

    const handleOpenExcel = () => {
        setExcelOpen(true);
        fetchExcelsByLead(id);
    };

    // ── reset helpers ──
    const resetPdf = () => {
        setPdfOpen(false);
        setSelectedPdf(null);
        setPdfNote("");
        setPdfNoteTouched(false);
    };

    const resetExcel = () => {
        setExcelOpen(false);
        setSelectedExcel(null);
        setExcelNote("");
        setExcelNoteTouched(false);
    };

    // ── submit handlers ──
    const handlePdfSubmit = async () => {
        if (!isNoteValid(pdfNote)) { setPdfNoteTouched(true); return; }
        try {
            await sendPdfMail({
                vendorLeadId: id,
                vendorQuotationId: selectedPdf._id,
                isQuoteSent: false,
                noteContent: pdfNote.trim(),
            }).unwrap();
            toast.success("Quotation PDF email sent successfully");
            resetPdf();
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not send PDF email");
        }
    };

    const handleExcelSubmit = async () => {
        if (!isNoteValid(excelNote)) { setExcelNoteTouched(true); return; }
        try {
            await sendExcelMail({
                vendorLeadId: id,
                vendorQuotationExcelId: selectedExcel._id,
                isQuoteSent: false,
                noteContent: excelNote.trim(),
            }).unwrap();
            toast.success("Quotation Excel email sent successfully");
            resetExcel();
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not send Excel email");
        }
    };

    if (vendorLeadDetail?.status !== "Negotiation") return null;

    return (
        <>
            <Box display="flex" gap={1}>
                <Button variant="outlined" size="small" onClick={handleOpenPdf}>
                    Requote PDF
                </Button>
                <Button variant="outlined" size="small" onClick={handleOpenExcel}>
                    Requote Excel
                </Button>
            </Box>

            <RequoteModal
                open={pdfOpen}
                onClose={resetPdf}
                title="Requote — Send PDF"
                options={quotationOptions}
                isFetching={fetchingQuotations}
                selectedValue={selectedPdf}
                onSelectionChange={setSelectedPdf}
                attachLabel="Attach Quotation (PDF)"
                noOptionsText="No quotations assigned to this lead"
                noteContent={pdfNote}
                setNoteContent={setPdfNote}
                noteTouched={pdfNoteTouched}
                setNoteTouched={setPdfNoteTouched}
                onSubmit={handlePdfSubmit}
                isLoading={sendingPdf}
            />

            <RequoteModal
                open={excelOpen}
                onClose={resetExcel}
                title="Requote — Send Excel"
                options={excelOptions}
                isFetching={fetchingExcels}
                selectedValue={selectedExcel}
                onSelectionChange={setSelectedExcel}
                attachLabel="Attach Quotation (Excel)"
                noOptionsText="No excel quotations assigned to this lead"
                noteContent={excelNote}
                setNoteContent={setExcelNote}
                noteTouched={excelNoteTouched}
                setNoteTouched={setExcelNoteTouched}
                onSubmit={handleExcelSubmit}
                isLoading={sendingExcel}
            />
        </>
    );
};

export default RequoteButtons;